import { PaginatedCollection, directusApi } from '@/api';
import { asyncForEach } from '@/utils';
import lodash from 'lodash';

export interface FeedbackInvoice {
    id: number;
    date_created: string;
    prodelec_order: string;
    error_info: string;
    invoice_data?: any;
    feedbacks?: any;
}

export interface FeedbackInvoiceState {
    activeFeedbackInvoice: FeedbackInvoice | null;
    activeFeedbackInvoiceLoading: boolean;
    allFeedbackInvoices: Array<FeedbackInvoice>;
    allFeedbackInvoicesLoading: boolean;
    invoiceFeedbacksPerPage: number;
    invoiceFeedbacksPage: number;
    invoiceFeedbacksTotal: number;
}

const state: FeedbackInvoiceState = {
    activeFeedbackInvoice: {
        id: 0,
        date_created: '',
        prodelec_order: '',
        error_info: '',
        invoice_data: {},
        feedbacks: []
    },
    activeFeedbackInvoiceLoading: false,
    allFeedbackInvoices: [],
    allFeedbackInvoicesLoading: false,
    invoiceFeedbacksPerPage: 1,
    invoiceFeedbacksPage: 1,
    invoiceFeedbacksTotal: 1
};

const mutations = {
    toggleAllFeedbackInvoicesLoading(state: FeedbackInvoiceState, data: boolean) {
        state.allFeedbackInvoicesLoading = data;
    },
    setAllFeedbackInvoices(state: FeedbackInvoiceState, data: PaginatedCollection) {
        state.allFeedbackInvoices = data.items;
        state.invoiceFeedbacksPerPage = data.perPage;
        state.invoiceFeedbacksPage = data.currentPage;
        state.invoiceFeedbacksTotal = data.totalItems;
    },
    toggleActiveFeedbackInvoiceLoading(state: FeedbackInvoiceState, data: boolean) {
        state.activeFeedbackInvoiceLoading = data;
    },
    setActiveFeedbackInvoice(state: FeedbackInvoiceState, data: FeedbackInvoice) {
        state.activeFeedbackInvoice = data;
    },
};

const actions = {
    async getAll({ _, commit }: any, { search, page, status_filter, start_date, end_date, }: any | null) {

        commit('toggleAllFeedbackInvoicesLoading', true);

        let limit = 10;
        let params = [];
        if (search) params.push(`search=${encodeURIComponent(search)}`);
        if (start_date) params.push(`start_date=${encodeURIComponent(start_date)}`);
        if (end_date) params.push(`end_date=${encodeURIComponent(end_date)}`);
        if (page) params.push(`limit=${limit}&offset=${(page - 1) * limit}`);

        let queryString = params.join('&');

        try {

            let endpoint = `custom/legacy-mercadolibre/feedbackInvoice?${queryString}`;
            let res: any = await directusApi('GET', endpoint);

            let feedback_invoices_data = [];
            let feedback_data = [];
            let all_feedback_invoices = [];
            let result: any;
            let countRegisters: any;

            if (res.data.data.length === 0) {
                endpoint = `custom/legacy-mercadolibre/dataInvoice?${queryString}`
                result = await directusApi('GET', endpoint);
                countRegisters = result.data.meta.filter_count;

                if (result.data.data.length > 0) {
                    feedback_data = lodash.orderBy(result.data.data, ['date_created'], ['desc']);

                    await asyncForEach(feedback_data, async (feedback: any) => {
                        await directusApi('GET', `items/feedback_invoices?&filter[prodelec_order][_eq]=${feedback.order}`).then(async feedbacks_by_order => {
                            feedback_invoices_data.push(feedbacks_by_order.data.data); // getting all records for every feedback
                        });
                    });

                    await asyncForEach(feedback_invoices_data, async (format: any) => {
                        if (format.length > 0) {
                            let desc_feedback = lodash.orderBy(format, ['date_created'], ['desc']);
                            let temp_id = desc_feedback[0].prodelec_order;
                            let temp_status = desc_feedback[0].error_info;
                            let temp_date = desc_feedback[0].date_created;
                            desc_feedback.shift();
                            all_feedback_invoices.push({
                                prodelec_order: temp_id,
                                status: temp_status,
                                date_created: temp_date,
                                feedbacks: desc_feedback.length > 0 ? [desc_feedback] : null,
                                variant: temp_status == 'SUCCESSFUL' ? 'success' : 'danger',
                                simple_status: temp_status == 'SUCCESSFUL' ? 'Finalizada' : 'Pendiente',
                                permalink: `https://www.mercadolibre.com.mx/ventas/${temp_id}/detalle`
                            });
                        }
                    });
                }
            } else {
                countRegisters = res.data.meta.filter_count;
                feedback_data = lodash.orderBy(res.data.data, ['date_created'], ['desc']);
              
                await asyncForEach(feedback_data, async (feedback: any) => {
                    await directusApi('GET', `items/feedback_invoices?&filter[prodelec_order][_eq]=${feedback.prodelec_order}`).then(async feedbacks_by_order => {
                        feedback_invoices_data.push(feedbacks_by_order.data.data); // getting all records for every feedback
                    });
                });
                await asyncForEach(feedback_invoices_data, async (format: any) => {
                    let desc_feedback = lodash.orderBy(format, ['date_created'], ['desc']);
                    let temp_id = desc_feedback[0].prodelec_order;
                    let temp_status = desc_feedback[0].error_info;
                    let temp_date = desc_feedback[0].date_created;
                    desc_feedback.shift();
                    all_feedback_invoices.push({
                        prodelec_order: temp_id,
                        status: temp_status,
                        date_created: temp_date,
                        feedbacks: desc_feedback.length > 0 ? [desc_feedback] : null,
                        variant: temp_status == 'SUCCESSFUL' ? 'success' : 'danger',
                        simple_status: temp_status == 'SUCCESSFUL' ? 'Finalizada' : 'Pendiente',
                        permalink: `https://www.mercadolibre.com.mx/ventas/${temp_id}/detalle`
                    });
                });
            }
            commit('setAllFeedbackInvoices', {
                items: all_feedback_invoices,
                perPage: limit,
                currentPage: page,
                totalItems: countRegisters
            });
        } catch (error) {
            console.error('Error fetching feedback invoices:', error);
        } finally {
            commit('toggleAllFeedbackInvoicesLoading', false);
        }
    },

    async getOne({ _, commit, dispatch }: any, id: any | null = null) {
        commit('toggleActiveFeedbackInvoiceLoading', true);
        let feedback_invoices_data = [];
        let invoice_info: any = {};
        await directusApi('GET', `items/feedback_invoices?&filter[prodelec_order][_eq]=${id}`).then(async feedbacks_by_order => {
            feedback_invoices_data = lodash.orderBy(feedbacks_by_order.data.data, ['date_created'], ['desc']); // getting all records for one feedback
            await directusApi('GET', `items/temp_xml?&filter[order][_eq]=${id}`).then(async xml => {
                let desc_xml = lodash.orderBy(xml.data.data, ['date_created'], ['desc']);
                invoice_info = {
                    id: desc_xml[0].order,
                    date_created: desc_xml[0].date_created,
                    invoice_data: desc_xml[0].data,
                    feedbacks: feedback_invoices_data
                };
            });
            commit('setActiveFeedbackInvoice', {
                id: invoice_info.id,
                date_created: invoice_info.date_created,
                invoice_data: invoice_info.invoice_data,
                feedbacks: invoice_info.feedbacks
            });
        }).catch(err => {
            console.log(err);
        }).finally(() => {
            commit('toggleActiveFeedbackInvoiceLoading', false);
        });
    },
    async sendInvoice({ _, commit, dispatch }: any, data : any | null) {
        commit('toggleActiveFeedbackInvoiceLoading', true);
        let id = data.id;
        let emails = data.emails;
        await directusApi('POST', 'custom/invoice/resend_invoice', { id, emails }).then(async res => {
            if (res.data.data.includes('Lo sentimos')) {
                data.view.$bvToast.toast(res.data.data, {
                    title: `Lo sentimos 😥 `,
                    variant: 'warning',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000,
                });
            } else {
                data.view.$bvToast.toast(res.data.data, {
                    title: `Documentos reenviados correctamente 😎`,
                    variant: 'success',
                    toaster: 'b-toaster-top-center',
                    autoHideDelay: 5000,
                });
            }
        }).catch(err => {
            console.log(err);
        }).finally(() => {
            commit('toggleActiveFeedbackInvoiceLoading', false);
        });
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};